import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FaWhatsapp } from 'react-icons/fa';
import { X } from 'lucide-react';

// Business WhatsApp number - used by the floating chat launcher + greeting.
const WHATSAPP_URL = 'https://wa.me/917842091427';

/*
 * Floating WhatsApp launcher with a one-time welcome popup. Popup auto-hides
 * after ~7 seconds or when the user dismisses / clicks through.
 */
export default function WhatsAppButton() {
  const [showPopup, setShowPopup] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setShowPopup(false), 7000);
    return () => clearTimeout(t);
  }, []);

  const open = () => {
    window.open(WHATSAPP_URL, '_blank', 'noopener,noreferrer');
    setShowPopup(false);
  };

  return (
    <>
      <AnimatePresence>
        {showPopup && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.96 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-24 right-4 z-50 max-w-[18rem] rounded-2xl bg-white dark:bg-ink-900 shadow-2xl ring-1 ring-ink-900/5 dark:ring-white/10 p-4"
          >
            <button
              aria-label="Dismiss"
              onClick={() => setShowPopup(false)}
              className="absolute right-2 top-2 rounded-full p-1 text-ink-400 hover:bg-ink-100 dark:hover:bg-white/10"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500 text-white">
                <FaWhatsapp className="w-4 h-4" />
              </span>
              <p className="font-semibold text-ink-900 dark:text-white">Hi there!</p>
            </div>
            <p className="mt-2 text-sm text-ink-600 dark:text-ink-200">
              Ready to chat with Vidhura Technologies? Tap below to start a WhatsApp conversation.
            </p>
            <button
              onClick={open}
              className="mt-3 w-full rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-medium py-2 transition-colors"
            >
              Chat on WhatsApp
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={open}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-4 right-4 z-50 inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-white shadow-xl ring-4 ring-emerald-500/20 hover:bg-emerald-600"
        aria-label="Chat with us on WhatsApp"
      >
        <FaWhatsapp className="w-7 h-7" />
      </motion.button>
    </>
  );
}
